import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { PlayCircle, FileText, PencilRuler, HelpCircle, Clock, Award, ChevronDown, Lock } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const ICONS = { video: PlayCircle, document: FileText, practice: PencilRuler, quiz: HelpCircle };

export default function CourseSales() {
  const { courseId } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [openMods, setOpenMods] = useState({});
  const [preview, setPreview] = useState(null);
  const [enrolling, setEnrolling] = useState(false);
  const [orderOpen, setOrderOpen] = useState(false);
  const [orderForm, setOrderForm] = useState({ name: "", email: "", whatsapp: "" });
  const [orderDone, setOrderDone] = useState(false);
  const [ordering, setOrdering] = useState(false);
  const navigate = useNavigate();
  const refCode = new URLSearchParams(window.location.search).get("ref");

  const submitOrder = async () => {
    if (!orderForm.name.trim() || !orderForm.email.trim()) return;
    setOrdering(true);
    try {
      await axios.post(`${API}/api/public/courses/${courseId}/order`, { ...orderForm, affiliate_code: refCode || null });
      setOrderDone(true);
    } catch (e) {
      alert(e?.response?.data?.detail || "Error enviando la orden");
    } finally {
      setOrdering(false);
    }
  };

  const enrollFree = async () => {
    setEnrolling(true);
    try {
      await axios.post(`${API}/api/my-courses/${courseId}/enroll-free`, {}, { withCredentials: true });
      navigate(`/dashboard/my-courses/${courseId}`);
    } catch (e) {
      if (e?.response?.status === 401) navigate(`/login`);
      else alert(e?.response?.data?.detail || "Error");
    } finally {
      setEnrolling(false);
    }
  };

  useEffect(() => {
    axios.get(`${API}/api/public/courses/${courseId}`)
      .then((r) => { setData(r.data); setOpenMods({ [r.data.modules[0]?.id]: true }); })
      .catch(() => setError(true));
  }, [courseId]);

  if (error) return <div className="min-h-screen flex items-center justify-center text-slate-500">Curso no disponible.</div>;
  if (!data) return <div className="min-h-screen flex items-center justify-center text-slate-400">Cargando…</div>;
  const { course, modules } = data;

  return (
    <div className="min-h-screen bg-white" data-testid="course-sales-page">
      {/* Hero */}
      <div className="bg-slate-900 text-white">
        <div className="max-w-5xl mx-auto px-4 py-10 grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
          <div>
            <Badge className="bg-orange-500 text-white border-0 mb-3">Curso on-demand</Badge>
            <h1 className="text-3xl md:text-4xl font-bold leading-tight">{course.title}</h1>
            {course.subtitle && <p className="text-slate-300 mt-3">{course.subtitle}</p>}
            <div className="flex flex-wrap items-center gap-3 mt-4 text-sm text-slate-300">
              {course.level && <Badge variant="outline" className="text-slate-200 border-slate-500">{course.level}</Badge>}
              <span>{course.lesson_count} lecciones</span>
              {course.total_minutes > 0 && <span className="inline-flex items-center gap-1"><Clock className="h-3.5 w-3.5" />{Math.round(course.total_minutes / 60 * 10) / 10} horas</span>}
              {course.certificate_enabled && <span className="inline-flex items-center gap-1"><Award className="h-3.5 w-3.5" />Certificado incluido</span>}
            </div>
            <div className="mt-6 flex items-center gap-4">
              <div className="text-3xl font-bold">
                {course.price === 0 ? "Gratis" : course.price != null ? <>${Number(course.price).toFixed(0)} <span className="text-sm font-normal text-slate-400">USD · pago único</span></> : ""}
              </div>
            </div>
            <Button className="mt-4 bg-orange-500 hover:bg-orange-600 text-white text-base px-8 py-6 rounded-xl" data-testid="buy-course-btn"
              onClick={course.price === 0 ? enrollFree : () => setOrderOpen(true)}>
              {course.price === 0 ? (enrolling ? "Inscribiendo…" : "Inscribirme gratis") : "Quiero este curso"}
            </Button>
          </div>
          {course.cover_url && (
            <div className="relative aspect-video rounded-xl overflow-hidden bg-slate-800 shadow-2xl">
              <img src={`${API}${course.cover_url}`} alt={course.title} className="absolute inset-0 h-full w-full object-cover" />
            </div>
          )}
        </div>
      </div>

      {/* Body */}
      <div className="max-w-5xl mx-auto px-4 py-10 grid grid-cols-1 md:grid-cols-3 gap-8">
        <div className="md:col-span-2 space-y-8">
          {course.description && (
            <section>
              <h2 className="text-lg font-semibold text-slate-900 mb-2">Sobre este curso</h2>
              <p className="text-slate-600 whitespace-pre-wrap">{course.description}</p>
            </section>
          )}
          <section data-testid="curriculum-section">
            <h2 className="text-lg font-semibold text-slate-900 mb-3">Contenido del curso</h2>
            <div className="space-y-2">
              {modules.map((m, mi) => (
                <div key={m.id} className="border border-slate-200 rounded-lg overflow-hidden">
                  <button type="button" className="w-full flex items-center justify-between px-4 py-3 bg-slate-50 text-left" onClick={() => setOpenMods((s) => ({ ...s, [m.id]: !s[m.id] }))} data-testid={`sales-module-${m.id}`}>
                    <span className="font-medium text-slate-800 text-sm">Módulo {mi + 1} — {m.title}</span>
                    <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform ${openMods[m.id] ? "rotate-180" : ""}`} />
                  </button>
                  {openMods[m.id] && (
                    <div className="divide-y divide-slate-100">
                      {m.lessons.map((l) => {
                        const Icon = ICONS[l.type] || FileText;
                        return (
                          <div key={l.id} className="flex items-center gap-3 px-4 py-2.5">
                            <Icon className="h-4 w-4 text-slate-400 shrink-0" />
                            <span className="text-sm text-slate-700 flex-1">{l.title}</span>
                            {l.duration_minutes ? <span className="text-xs text-slate-400">{l.duration_minutes} min</span> : null}
                            {l.is_free_preview ? (
                              <Button size="sm" variant="outline" className="h-7 text-xs text-emerald-700 border-emerald-300" onClick={() => setPreview(l)} data-testid={`free-preview-${l.id}`}>
                                Ver gratis
                              </Button>
                            ) : (
                              <Lock className="h-3.5 w-3.5 text-slate-300" />
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </section>
        </div>

        <aside className="space-y-4" id="buy-info">
          <div className="border border-slate-200 rounded-xl p-5 shadow-sm sticky top-6">
            <div className="text-2xl font-bold text-slate-900">{course.price === 0 ? "Gratis" : course.price != null ? `$${Number(course.price).toFixed(0)} USD` : ""}</div>
            <ul className="text-sm text-slate-600 mt-3 space-y-1.5">
              <li>✓ Acceso de por vida</li>
              <li>✓ Estudiá a tu propio ritmo</li>
              <li>✓ Quizzes y prácticas incluidas</li>
              {course.certificate_enabled && <li>✓ Certificado al completar</li>}
            </ul>
            {course.price === 0 ? (
              <div className="mt-4 p-3 rounded-lg bg-emerald-50 border border-emerald-200 text-xs text-emerald-800" data-testid="free-note">
                Este curso es gratuito — hacé clic en "Inscribirme gratis" para empezar ya.
              </div>
            ) : (
              <Button className="w-full mt-4 bg-orange-500 hover:bg-orange-600 text-white" onClick={() => setOrderOpen(true)} data-testid="sidebar-buy-btn">
                Quiero este curso
              </Button>
            )}
            <Link to="/login" className="block mt-3 text-center text-xs text-slate-400 hover:text-slate-600">¿Ya tenés acceso? Iniciá sesión</Link>
          </div>
        </aside>
      </div>

      {/* Order dialog */}
      <Dialog open={orderOpen} onOpenChange={(o) => { setOrderOpen(o); if (!o) setOrderDone(false); }}>
        <DialogContent className="max-w-md" data-testid="order-dialog">
          <DialogTitle>{orderDone ? "¡Orden recibida! 🎉" : `Solicitar acceso — $${Number(course.price || 0).toFixed(0)} USD`}</DialogTitle>
          {orderDone ? (
            <div className="space-y-3 text-sm text-slate-600" data-testid="order-success">
              <p>Te enviamos un correo con la confirmación. Nuestro equipo te contactará con las instrucciones de pago (transferencia o SINPE Móvil) y apenas confirmemos el pago activaremos tu acceso.</p>
              <Button className="w-full bg-orange-500 hover:bg-orange-600 text-white" onClick={() => setOrderOpen(false)}>Entendido</Button>
            </div>
          ) : (
            <div className="space-y-3">
              <input className="w-full h-10 px-3 rounded-md border border-slate-300 text-sm" placeholder="Nombre completo *" value={orderForm.name} onChange={(e) => setOrderForm({ ...orderForm, name: e.target.value })} data-testid="order-name" />
              <input className="w-full h-10 px-3 rounded-md border border-slate-300 text-sm" type="email" placeholder="Email *" value={orderForm.email} onChange={(e) => setOrderForm({ ...orderForm, email: e.target.value })} data-testid="order-email" />
              <input className="w-full h-10 px-3 rounded-md border border-slate-300 text-sm" placeholder="WhatsApp (opcional)" value={orderForm.whatsapp} onChange={(e) => setOrderForm({ ...orderForm, whatsapp: e.target.value })} data-testid="order-whatsapp" />
              <p className="text-[11px] text-slate-400">Al enviar, recibirás un email con las instrucciones de pago. El acceso se activa al confirmar el pago.</p>
              <Button className="w-full bg-orange-500 hover:bg-orange-600 text-white" onClick={submitOrder} disabled={ordering || !orderForm.name.trim() || !orderForm.email.trim()} data-testid="order-submit">
                {ordering ? "Enviando…" : "Enviar solicitud"}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Free preview modal */}
      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent className="max-w-3xl" data-testid="free-preview-dialog">
          <DialogTitle>{preview?.title}</DialogTitle>
          {preview?.type === "video" && preview?.youtube_id && (
            <div className="relative aspect-video rounded-lg overflow-hidden bg-black">
              <iframe className="absolute inset-0 h-full w-full" src={`https://www.youtube-nocookie.com/embed/${preview.youtube_id}?rel=0&modestbranding=1`} title={preview.title} allowFullScreen allow="autoplay; encrypted-media" />
            </div>
          )}
          {preview?.type === "practice" && <div className="text-sm text-slate-700 whitespace-pre-wrap">{preview.body}</div>}
          {preview?.type === "document" && preview?.doc_url && (
            <a href={preview.doc_url} target="_blank" rel="noreferrer" className="text-brand-blue underline text-sm">Abrir documento</a>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
